import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Label } from '../../components/Label';
import { SUBMITTED_FORM_LABELS } from '@epics/taskFormManagement/constants/labels';
import {
  RefreshCw,
  Tool,
  Award,
  ChevronRight,
  ChevronDown,
} from 'react-feather';

/** Certificate medal icon — filled when active (on page), outlined when inactive. */
const CertificateNavIcon = ({ active }: { active: boolean }) =>
  active ? (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="12" cy="8" r="7" fill="currentColor" />
      <path d="M8.21 13.89L7 23L12 20L17 23L15.79 13.88" fill="currentColor" />
    </svg>
  ) : (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="8" r="7" />
      <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" />
    </svg>
  );
import {
  InboxIcon as InboxOutline,
  WrenchScrewdriverIcon as WrenchScrewdriverOutline,
  DocumentDuplicateIcon as DocumentDuplicateOutline,
  ClipboardDocumentListIcon as ClipboardDocumentListOutline,
  Squares2X2Icon as Squares2x2Outline,
  DocumentTextIcon as DocumentTextOutline,
} from '@heroicons/react/24/outline';
import {
  InboxIcon as InboxSolid,
  WrenchScrewdriverIcon as WrenchScrewdriverSolid,
  DocumentDuplicateIcon as DocumentDuplicateSolid,
  ClipboardDocumentListIcon as ClipboardDocumentListSolid,
  Squares2X2Icon as Squares2x2Solid,
} from '@heroicons/react/24/solid';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUserRole } from '../../hooks/useUserRole';
import { useNavHighlightStore } from '../../store/navHighlightStore';
import { CertificateSpotlight } from './CertificateSpotlight';
import { SideNavFlyout } from '../../components/SideNavFlyout';

const UserSideNavIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M15.75 6.5C15.75 8.57107 14.0711 10.25 12 10.25C9.92893 10.25 8.25 8.57107 8.25 6.5C8.25 4.42893 9.92893 2.75 12 2.75C14.0711 2.75 15.75 4.42893 15.75 6.5Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <path
      d="M12.0011 13.25C8.60997 13.25 6.03711 15.2643 4.9836 18.1129C4.5748 19.2182 5.51944 20.25 6.69796 20.25H17.3043C18.4828 20.25 19.4274 19.2182 19.0186 18.1129C17.9651 15.2643 15.3923 13.25 12.0011 13.25Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
  </svg>
);

const UserSideNavIconFilled = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 2C9.51472 2 7.5 4.01472 7.5 6.5C7.5 8.98528 9.51472 11 12 11C14.4853 11 16.5 8.98528 16.5 6.5C16.5 4.01472 14.4853 2 12 2Z"
      fill="currentColor"
    />
    <path
      d="M12.0014 12.5C8.27968 12.5 5.43692 14.7258 4.28043 17.8527C3.97401 18.6813 4.18231 19.4953 4.67127 20.078C5.14989 20.6484 5.89317 21 6.69823 21H17.3046C18.1096 21 18.8529 20.6484 19.3315 20.078C19.8205 19.4953 20.0288 18.6813 19.7223 17.8527C18.5659 14.7258 15.7231 12.5 12.0014 12.5Z"
      fill="currentColor"
    />
  </svg>
);

const BuildingIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3.75 19.25V5.75C3.75 4.64543 4.64543 3.75 5.75 3.75H12.25C13.3546 3.75 14.25 4.64543 14.25 5.75V19.25"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M7.75 8.75H10.25"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M7.75 12.75H10.25"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M22.25 19.25H1.75"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M14.25 7.75H18.25C19.3546 7.75 20.25 8.64543 20.25 9.75V19.25"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const BuildingIconFilled = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M5.75 3C4.23122 3 3 4.23122 3 5.75V18.5H1.75C1.33579 18.5 1 18.8358 1 19.25C1 19.6642 1.33579 20 1.75 20H22.25C22.6642 20 23 19.6642 23 19.25C23 18.8358 22.6642 18.5 22.25 18.5H21V9.75C21 8.23122 19.7688 7 18.25 7H16V18.5H15V5.75C15 4.23122 13.7688 3 12.25 3H5.75ZM7.75 8C7.33579 8 7 8.33579 7 8.75C7 9.16421 7.33579 9.5 7.75 9.5H10.25C10.6642 9.5 11 9.16421 11 8.75C11 8.33579 10.6642 8 10.25 8H7.75ZM7.75 12C7.33579 12 7 12.3358 7 12.75C7 13.1642 7.33579 13.5 7.75 13.5H10.25C10.6642 13.5 11 13.1642 11 12.75C11 12.3358 10.6642 12 10.25 12H7.75Z"
      fill="currentColor"
    />
  </svg>
);

const UsersIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3.49805 7C3.49805 4.79086 5.28891 3 7.49805 3C9.70719 3 11.498 4.79086 11.498 7C11.498 9.20914 9.70719 11 7.49805 11C5.28891 11 3.49805 9.20914 3.49805 7Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M15.6902 17.557C15.2133 15.7412 14.3683 14.2309 13.2812 13.0797C14.3575 12.3664 15.5816 12 16.8265 12C19.7829 12 22.6223 14.066 23.6392 17.9381C24.0848 19.6346 22.653 21 21.1092 21H14.9316C15.6748 20.1309 16.0524 18.9363 15.6902 17.557Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M16.498 3C14.2889 3 12.498 4.79086 12.498 7C12.498 9.20914 14.2889 11 16.498 11C18.7072 11 20.498 9.20914 20.498 7C20.498 4.79086 18.7072 3 16.498 3Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M7.42649 12C10.3829 12 13.2222 14.066 14.2392 17.9381C14.6848 19.6346 13.2529 21 11.7091 21H3.14386C1.60005 21 0.16821 19.6346 0.613794 17.9381C1.63077 14.066 4.47009 12 7.42649 12Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const UsersIconFilled = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3.49805 7C3.49805 4.79086 5.28891 3 7.49805 3C9.70719 3 11.498 4.79086 11.498 7C11.498 9.20914 9.70719 11 7.49805 11C5.28891 11 3.49805 9.20914 3.49805 7Z"
      fill="currentColor"
    />
    <path
      d="M15.6902 17.557C15.2133 15.7412 14.3683 14.2309 13.2812 13.0797C14.3575 12.3664 15.5816 12 16.8265 12C19.7829 12 22.6223 14.066 23.6392 17.9381C24.0848 19.6346 22.653 21 21.1092 21H14.9316C15.6748 20.1309 16.0524 18.9363 15.6902 17.557Z"
      fill="currentColor"
    />
    <path
      d="M16.498 3C14.2889 3 12.498 4.79086 12.498 7C12.498 9.20914 14.2889 11 16.498 11C18.7072 11 20.498 9.20914 20.498 7C20.498 4.79086 18.7072 3 16.498 3Z"
      fill="currentColor"
    />
    <path
      d="M7.42649 12C10.3829 12 13.2222 14.066 14.2392 17.9381C14.6848 19.6346 13.2529 21 11.7091 21H3.14386C1.60005 21 0.16821 19.6346 0.613794 17.9381C1.63077 14.066 4.47009 12 7.42649 12Z"
      fill="currentColor"
    />
  </svg>
);

const PlansIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25ZM6.75 12h.008v.008H6.75V12Zm0 3h.008v.008H6.75V15Zm0 3h.008v.008H6.75V18Z"
      stroke="currentColor"
      strokeWidth="1.5"
    />
  </svg>
);

const PlansIconFilled = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M7.502 6H14.63C15.5242 6.00132 16.3814 6.35749 17.0133 6.99028C17.6451 7.62307 18 8.48076 18 9.375V18.75C18.7956 18.75 19.5587 18.4339 20.1213 17.8713C20.6839 17.3087 21 16.5456 21 15.75V6.108C21 4.603 19.875 3.297 18.336 3.168C18.1118 3.14979 17.8874 3.13312 17.663 3.118C17.4099 2.63029 17.0276 2.22147 16.558 1.93614C16.0884 1.65082 15.5495 1.49995 15 1.5H13.5C12.9505 1.49995 12.4116 1.65082 11.942 1.93614C11.4724 2.22147 11.0901 2.63029 10.837 3.118C10.612 3.133 10.387 3.15 10.164 3.168C8.662 3.295 7.554 4.542 7.502 6ZM13.5 3C13.1022 3 12.7206 3.15804 12.4393 3.43934C12.158 3.72064 12 4.10218 12 4.5H16.5C16.5 4.10218 16.342 3.72064 16.0607 3.43934C15.7794 3.15804 15.3978 3 15 3H13.5Z"
      fill="#223746"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M3 9.375C3 8.339 3.84 7.5 4.875 7.5H14.625C15.661 7.5 16.5 8.34 16.5 9.375V20.625C16.5 21.66 15.66 22.5 14.625 22.5H4.875C4.37772 22.5 3.90081 22.3025 3.54917 21.9508C3.19754 21.5992 3 21.1223 3 20.625V9.375ZM6 12C6 11.8011 6.07902 11.6103 6.21967 11.4697C6.36032 11.329 6.55109 11.25 6.75 11.25H6.758C6.95691 11.25 7.14768 11.329 7.28833 11.4697C7.42898 11.6103 7.508 11.8011 7.508 12V12.008C7.508 12.2069 7.42898 12.3977 7.28833 12.5383C7.14768 12.679 6.95691 12.758 6.758 12.758H6.75C6.55109 12.758 6.36032 12.679 6.21967 12.5383C6.07902 12.3977 6 12.2069 6 12.008V12ZM8.25 12C8.25 11.8011 8.32902 11.6103 8.46967 11.4697C8.61032 11.329 8.80109 11.25 9 11.25H12.75C12.9489 11.25 13.1397 11.329 13.2803 11.4697C13.421 11.6103 13.5 11.8011 13.5 12C13.5 12.1989 13.421 12.3897 13.2803 12.5303C13.1397 12.671 12.9489 12.75 12.75 12.75H9C8.80109 12.75 8.61032 12.671 8.46967 12.5303C8.32902 12.3897 8.25 12.1989 8.25 12ZM6 15C6 14.8011 6.07902 14.6103 6.21967 14.4697C6.36032 14.329 6.55109 14.25 6.75 14.25H6.758C6.95691 14.25 7.14768 14.329 7.28833 14.4697C7.42898 14.6103 7.508 14.8011 7.508 15V15.008C7.508 15.2069 7.42898 15.3977 7.28833 15.5383C7.14768 15.679 6.95691 15.758 6.758 15.758H6.75C6.55109 15.758 6.36032 15.679 6.21967 15.5383C6.07902 15.3977 6 15.2069 6 15.008V15ZM8.25 15C8.25 14.8011 8.32902 14.6103 8.46967 14.4697C8.61032 14.329 8.80109 14.25 9 14.25H12.75C12.9489 14.25 13.1397 14.329 13.2803 14.4697C13.421 14.6103 13.5 14.8011 13.5 15C13.5 15.1989 13.421 15.3897 13.2803 15.5303C13.1397 15.671 12.9489 15.75 12.75 15.75H9C8.80109 15.75 8.61032 15.671 8.46967 15.5303C8.32902 15.3897 8.25 15.1989 8.25 15ZM6 18C6 17.8011 6.07902 17.6103 6.21967 17.4697C6.36032 17.329 6.55109 17.25 6.75 17.25H6.758C6.95691 17.25 7.14768 17.329 7.28833 17.4697C7.42898 17.6103 7.508 17.8011 7.508 18V18.008C7.508 18.2069 7.42898 18.3977 7.28833 18.5383C7.14768 18.679 6.95691 18.758 6.758 18.758H6.75C6.55109 18.758 6.36032 18.679 6.21967 18.5383C6.07902 18.3977 6 18.2069 6 18.008V18ZM8.25 18C8.25 17.8011 8.32902 17.6103 8.46967 17.4697C8.61032 17.329 8.80109 17.25 9 17.25H12.75C12.9489 17.25 13.1397 17.329 13.2803 17.4697C13.421 17.6103 13.5 17.8011 13.5 18C13.5 18.1989 13.421 18.3897 13.2803 18.5303C13.1397 18.671 12.9489 18.75 12.75 18.75H9C8.80109 18.75 8.61032 18.671 8.46967 18.5303C8.32902 18.3897 8.25 18.1989 8.25 18Z"
      fill="#223746"
    />
  </svg>
);

const OffersIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9.568 3H5.25C4.65326 3 4.08097 3.23705 3.65901 3.65901C3.23705 4.08097 3 4.65326 3 5.25V9.568C3 10.165 3.237 10.738 3.659 11.159L13.24 20.74C13.939 21.439 15.02 21.612 15.847 21.07C17.9286 19.7066 19.7066 17.9286 21.07 15.847C21.612 15.02 21.439 13.939 20.74 13.24L11.16 3.66C10.951 3.45077 10.7029 3.28478 10.4297 3.17154C10.1565 3.05829 9.86371 3 9.568 3Z"
      stroke="var(--Schemes-On-Surface-Variant, #474A4C)"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6 6H6.008V6.008H6V6Z"
      stroke="var(--Schemes-On-Surface-Variant, #474A4C)"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const OffersIconFilled = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9.568 3H5.25C4.65326 3 4.08097 3.23705 3.65901 3.65901C3.23705 4.08097 3 4.65326 3 5.25V9.568C3 10.165 3.237 10.738 3.659 11.159L13.24 20.74C13.939 21.439 15.02 21.612 15.847 21.07C17.9286 19.7066 19.7066 17.9286 21.07 15.847C21.612 15.02 21.439 13.939 20.74 13.24L11.16 3.66C10.951 3.45077 10.7029 3.28478 10.4297 3.17154C10.1565 3.05829 9.86371 3 9.568 3Z"
      fill="currentColor"
    />
    <path d="M6 6H6.008V6.008H6V6Z" fill="currentColor" />
  </svg>
);

const ServiceTypeIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9.568 3H5.25C4.65326 3 4.08097 3.23705 3.65901 3.65901C3.23705 4.08097 3 4.65326 3 5.25V9.568C3 10.165 3.237 10.738 3.659 11.159L13.24 20.74C13.939 21.439 15.02 21.612 15.847 21.07C17.9286 19.7066 19.7066 17.9286 21.07 15.847C21.612 15.02 21.439 13.939 20.74 13.24L11.16 3.66C10.951 3.45077 10.7029 3.28478 10.4297 3.17154C10.1565 3.05829 9.86371 3 9.568 3Z"
      stroke="#181A1B"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6 6H6.008V6.008H6V6Z"
      stroke="#181A1B"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ServiceTypeIconFilled = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M5.25 2.25C4.45435 2.25 3.69129 2.56607 3.12868 3.12868C2.56607 3.69129 2.25 4.45435 2.25 5.25V9.568C2.25017 10.3636 2.56635 11.1265 3.129 11.689L12.709 21.27C13.629 22.19 15.099 22.456 16.257 21.698C18.4254 20.2781 20.2777 18.4261 21.698 16.258C22.456 15.098 22.19 13.629 21.27 12.71L11.69 3.129C11.4114 2.85029 11.0806 2.6292 10.7165 2.47838C10.3524 2.32756 9.9621 2.24995 9.568 2.25H5.25ZM6.375 7.5C6.67337 7.5 6.95952 7.38147 7.1705 7.1705C7.38147 6.95952 7.5 6.67337 7.5 6.375C7.5 6.07663 7.38147 5.79048 7.1705 5.5795C6.95952 5.36853 6.67337 5.25 6.375 5.25C6.07663 5.25 5.79048 5.36853 5.5795 5.5795C5.36853 5.79048 5.25 6.07663 5.25 6.375C5.25 6.67337 5.36853 6.95952 5.5795 7.1705C5.79048 7.38147 6.07663 7.5 6.375 7.5Z"
      fill="#181A1B"
    />
  </svg>
);

export type SideNavContentProps = {
  /** When true the labels render icon-only (collapsed desktop sidebar).
   *  The tablet drawer always passes false here. */
  isIconOnly: boolean;
};

const NAV_HEIGHT: React.CSSProperties = { height: '56px' };

const SideNavContent: React.FC<SideNavContentProps> = ({ isIconOnly }) => {
  const itemStyle = isIconOnly ? { width: 56, height: 56 } : NAV_HEIGHT;
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoading, isTechnician, isClient, role } = useUserRole();
  const isAdminOrSuperAdmin = role === 'ADMIN' || role === 'SUPER_ADMIN';
  const decodedPathname = decodeURIComponent(location.pathname);

  const isOnDashboard = decodedPathname.includes('/Panel-główny');
  const isOnPlansPage = decodedPathname.includes('/Plany');
  const isOnPlanListPage =
    decodedPathname === '/Plany' || decodedPathname === '/Plany/';
  const isOnOffersPage =
    decodedPathname.includes('/Oferty') || decodedPathname.includes('/Offers');
  const isOnSubscriptionSubPage = isOnPlansPage || isOnOffersPage;

  /**
   * Renders the "Plany" and "Oferty" sub-items shared between the inline
   * expanded sub-nav and the collapsed-sidebar flyout.
   *
   * `onAfterClick` lets each caller control what happens right before
   * navigation: the inline (expanded) rendering passes a no-op so today's
   * behavior — the Subskrypcje section stays open after a sub-item click —
   * is unchanged, while the flyout rendering passes a callback that closes
   * the flyout.
   */
  const renderSubscriptionsSubItems = (onAfterClick: () => void) => (
    <>
      <Label
        size="Large"
        labelType="Square"
        leftIcon={isOnPlansPage ? <PlansIconFilled /> : <PlansIcon />}
        isIconOnly={false}
        selected={isOnPlansPage}
        style={{ height: '56px', alignSelf: 'stretch' }}
        onClick={() => {
          onAfterClick();
          if (isOnPlanListPage) {
            window.dispatchEvent(
              new CustomEvent('subscription-list-close-popups')
            );
            return;
          }
          navigate('/Plany', { state: { freshStart: true } });
        }}
      >
        Plany
      </Label>

      <Label
        size="Large"
        labelType="Square"
        leftIcon={isOnOffersPage ? <OffersIconFilled /> : <OffersIcon />}
        isIconOnly={false}
        selected={isOnOffersPage}
        style={{ height: '56px', alignSelf: 'stretch' }}
        onClick={() => {
          onAfterClick();
          navigate(
            { pathname: '/Oferty', search: '' },
            { state: { freshStart: true } }
          );
        }}
      >
        Oferty
      </Label>
    </>
  );
  const isOnTechniciansPage = decodedPathname.includes('/technicians');
  const isOnClientsPage = decodedPathname.includes('/Klienci');
  const isOnFacilitiesPage = decodedPathname.includes('/Placówki');
  const isOnServiceTypesPage = decodedPathname.includes('/Typy-usług');
  const isOnZgłoszeniaPage = decodedPathname.includes('/Zgłoszenia');
  const isOnProductsPage = decodedPathname.includes('/Produkty i usługi');
  const isOnFormularzeZadańPage = decodedPathname.includes('/Formularze-zadań');
  const isOnCertyfikatyPage = decodedPathname.includes('/Certyfikaty');
  const isOnMyTasksPage =
    decodedPathname.includes('/Moje-zadania') ||
    decodedPathname.includes('/Zgłoszenia/Podgląd-zgłoszenia');
  const isOnSubmittedFormsPage = decodedPathname.includes(
    '/Przesłane-formularze'
  );
  const isOnMyCertificatesPage = decodedPathname.includes('/Moje-certyfikaty');
  const isOnKategoriePage = decodedPathname.includes('/Kategorie');

  const [isSubscriptionsExpanded, setIsSubscriptionsExpanded] = useState(
    isOnSubscriptionSubPage
  );
  const subskrypcjeTriggerRef = useRef<HTMLDivElement>(null);
  const [isAktywnościExpanded, setIsAktywnościExpanded] =
    useState(isOnKategoriePage);
  const szjTriggerRef = useRef<HTMLDivElement>(null);

  /**
   * Renders the "Kategorie" sub-item shared between the inline expanded
   * sub-nav and the collapsed-sidebar flyout.
   *
   * `onAfterClick` mirrors `renderSubscriptionsSubItems` above: the inline
   * (expanded) rendering passes a no-op so today's behavior — the SZJ
   * section stays open after a sub-item click — is unchanged, while the
   * flyout rendering passes a callback that closes the flyout.
   */
  const renderSzjSubItems = (onAfterClick: () => void) => (
    <Label
      size="Large"
      labelType="Square"
      leftIcon={
        isOnKategoriePage ? (
          <Squares2x2Solid className="size-6" />
        ) : (
          <Squares2x2Outline className="size-6" strokeWidth={1.5} />
        )
      }
      isIconOnly={false}
      selected={isOnKategoriePage}
      style={{ height: '56px', alignSelf: 'stretch' }}
      onClick={() => {
        onAfterClick();
        navigate('/Kategorie', { state: { freshStart: true } });
      }}
    >
      Kategorie
    </Label>
  );

  useEffect(() => {
    if (isOnSubscriptionSubPage) {
      setIsSubscriptionsExpanded(true);
    }
  }, [isOnSubscriptionSubPage]);

  useEffect(() => {
    if (isOnKategoriePage) {
      setIsAktywnościExpanded(true);
    }
  }, [isOnKategoriePage]);

  const hasCertHighlight = useNavHighlightStore((s) =>
    s.hasHighlight('certificates')
  );
  const clearCertHighlight = useNavHighlightStore((s) => s.clearHighlight);
  const certRef = useRef<HTMLDivElement>(null);
  const [showCertSpotlight, setShowCertSpotlight] = useState(false);

  useEffect(() => {
    if (!hasCertHighlight || !isTechnician) {
      setShowCertSpotlight(false);
      return;
    }
    // Slight delay so the success snackbar is visible first
    const t = setTimeout(() => setShowCertSpotlight(true), 800);
    return () => clearTimeout(t);
  }, [hasCertHighlight, isTechnician]);

  const handleDismissCertSpotlight = useCallback(() => {
    setShowCertSpotlight(false);
    clearCertHighlight('certificates');
  }, [clearCertHighlight]);

  // Prevent flash of wrong nav while role is loading
  if (isLoading) {
    return null;
  }

  const dashboardLabel = (
    <Label
      size="Large"
      labelType="Square"
      leftIcon={
        isOnDashboard ? (
          <img src="/assets/home_filled.svg" alt="Home" className="size-6" />
        ) : (
          <img src="/assets/home.svg" alt="Home" className="size-6" />
        )
      }
      isIconOnly={isIconOnly}
      style={itemStyle}
      onClick={() => navigate('/Panel-główny', { state: { freshStart: true } })}
      selected={isOnDashboard}
    >
      Panel główny
    </Label>
  );

  if (isTechnician) {
    return (
      <>
        <div className="flex flex-col gap-[2px]">
          {dashboardLabel}

          <Label
            size="Large"
            labelType={isIconOnly ? 'Round' : 'Square'}
            leftIcon={
              isOnMyTasksPage ? (
                <ClipboardDocumentListSolid className="size-6" />
              ) : (
                <ClipboardDocumentListOutline
                  className="size-6"
                  strokeWidth={1.5}
                />
              )
            }
            isIconOnly={isIconOnly}
            selected={isOnMyTasksPage}
            style={itemStyle}
            onClick={() =>
              navigate('/Moje-zadania', { state: { freshStart: true } })
            }
          >
            Moje zadania
          </Label>

          <Label
            size="Large"
            labelType={isIconOnly ? 'Round' : 'Square'}
            leftIcon={
              isOnSubmittedFormsPage ? (
                <DocumentDuplicateSolid className="size-6" />
              ) : (
                <DocumentDuplicateOutline
                  className="size-6"
                  strokeWidth={1.5}
                />
              )
            }
            isIconOnly={isIconOnly}
            selected={isOnSubmittedFormsPage}
            style={itemStyle}
            onClick={() =>
              navigate('/Przesłane-formularze', { state: { freshStart: true } })
            }
          >
            {SUBMITTED_FORM_LABELS.LIST_TITLE}
          </Label>

          <div ref={certRef} className="w-full flex flex-col">
            <Label
              size="Large"
              labelType={isIconOnly ? 'Round' : 'Square'}
              leftIcon={<CertificateNavIcon active={isOnMyCertificatesPage} />}
              isIconOnly={isIconOnly}
              selected={isOnMyCertificatesPage}
              style={itemStyle}
              onClick={() => {
                navigate('/Moje-certyfikaty', { state: { freshStart: true } });
              }}
            >
              Certyfikaty
            </Label>
          </div>
        </div>
        {showCertSpotlight && (
          <CertificateSpotlight
            anchorEl={certRef.current}
            onDismiss={handleDismissCertSpotlight}
          />
        )}
      </>
    );
  }

  if (isClient) {
    return (
      <div className="flex flex-col gap-[2px]">
        {dashboardLabel}

        <Label
          size="Large"
          labelType="Square"
          leftIcon={
            isOnFacilitiesPage ? (
              <img
                src="/assets/facilities_filled.svg"
                alt="Placówki"
                className="size-6"
              />
            ) : (
              <img
                src="/assets/facilities.svg"
                alt="Placówki"
                className="size-6"
              />
            )
          }
          isIconOnly={isIconOnly}
          selected={isOnFacilitiesPage}
          style={itemStyle}
          onClick={() => navigate('/Placówki', { state: { freshStart: true } })}
        >
          Placówki
        </Label>

        <Label
          size="Large"
          labelType="Square"
          leftIcon={<Tool size={24} strokeWidth={1.5} />}
          isIconOnly={isIconOnly}
          style={itemStyle}
        >
          Zgłoszenia
        </Label>

        <Label
          size="Large"
          labelType="Square"
          leftIcon={
            isOnProductsPage ? (
              <WrenchScrewdriverSolid className="size-6" />
            ) : (
              <WrenchScrewdriverOutline className="size-6" strokeWidth={1.5} />
            )
          }
          isIconOnly={isIconOnly}
          selected={isOnProductsPage}
          style={itemStyle}
          onClick={() =>
            navigate('/Produkty i usługi', { state: { freshStart: true } })
          }
        >
          Produkty i usługi
        </Label>

        <Label
          size="Large"
          labelType="Square"
          leftIcon={
            <CertificateNavIcon
              active={isOnCertyfikatyPage || isOnMyCertificatesPage}
            />
          }
          isIconOnly={isIconOnly}
          selected={isOnCertyfikatyPage || isOnMyCertificatesPage}
          style={itemStyle}
          onClick={() =>
            navigate('/Certyfikaty', { state: { freshStart: true } })
          }
        >
          Certyfikaty
        </Label>
      </div>
    );
  }

  // Default — admin / superadmin / all other roles
  return (
    <div className="flex flex-col gap-[2px]">
      {dashboardLabel}

      <Label
        size="Large"
        labelType="Square"
        leftIcon={
          location.pathname.includes('/administrators') ? (
            <UsersIconFilled />
          ) : (
            <UsersIcon />
          )
        }
        isIconOnly={isIconOnly}
        selected={location.pathname.includes('/administrators')}
        style={itemStyle}
        onClick={() =>
          navigate('/administrators?page=1&limit=10', {
            replace: true,
            state: { freshStart: true },
          })
        }
      >
        Administratorzy
      </Label>

      <Label
        size="Large"
        labelType="Square"
        leftIcon={isOnClientsPage ? <BuildingIconFilled /> : <BuildingIcon />}
        isIconOnly={isIconOnly}
        selected={isOnClientsPage}
        style={itemStyle}
        onClick={() => navigate('/Klienci', { state: { freshStart: true } })}
      >
        Klienci
      </Label>

      <Label
        size="Large"
        labelType="Square"
        leftIcon={
          isOnTechniciansPage ? <UserSideNavIconFilled /> : <UserSideNavIcon />
        }
        isIconOnly={isIconOnly}
        selected={isOnTechniciansPage}
        style={itemStyle}
        onClick={() =>
          navigate('/technicians', { state: { freshStart: true } })
        }
      >
        Technicy
      </Label>

      <div ref={subskrypcjeTriggerRef}>
        <Label
          size="Large"
          labelType="Square"
          leftIcon={<RefreshCw size={24} strokeWidth={1.5} />}
          rightIcon={
            isSubscriptionsExpanded ? (
              <ChevronDown size={24} />
            ) : (
              <ChevronRight size={24} />
            )
          }
          isIconOnly={isIconOnly}
          selected={false}
          state="Enabled"
          style={isIconOnly ? itemStyle : { ...itemStyle, width: '100%' }}
          onClick={() => {
            const next = !isSubscriptionsExpanded;
            setIsSubscriptionsExpanded(next);
            if (next) {
              setIsAktywnościExpanded(false);
            }
          }}
        >
          Subskrypcje
        </Label>
      </div>

      {isSubscriptionsExpanded && !isIconOnly && (
        <div
          className="SubscriptionsSubNav"
          style={{
            width: '100%',
            paddingLeft: '48px',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            gap: '2px',
            display: 'flex',
            position: 'relative',
          }}
        >
          <div
            className="border-l-2 border-[#DCDDE0] shrink-0"
            style={{
              position: 'absolute',
              left: '20px',
              top: 0,
              height: '114px',
            }}
          />
          {renderSubscriptionsSubItems(() => {})}
        </div>
      )}

      <SideNavFlyout
        open={isSubscriptionsExpanded && isIconOnly}
        triggerRef={subskrypcjeTriggerRef}
        onClose={() => setIsSubscriptionsExpanded(false)}
      >
        {renderSubscriptionsSubItems(() => setIsSubscriptionsExpanded(false))}
      </SideNavFlyout>

      <Label
        size="Large"
        labelType="Square"
        leftIcon={
          isOnServiceTypesPage ? <ServiceTypeIconFilled /> : <ServiceTypeIcon />
        }
        isIconOnly={isIconOnly}
        selected={isOnServiceTypesPage}
        style={itemStyle}
        onClick={() => navigate('/Typy-usług', { state: { freshStart: true } })}
      >
        Typy usług
      </Label>

      <div ref={szjTriggerRef}>
        <Label
          size="Large"
          labelType="Square"
          leftIcon={
            <DocumentTextOutline className="size-6" strokeWidth={1.5} />
          }
          rightIcon={
            isAktywnościExpanded ? (
              <ChevronDown size={24} />
            ) : (
              <ChevronRight size={24} />
            )
          }
          isIconOnly={isIconOnly}
          selected={false}
          state="Enabled"
          style={isIconOnly ? itemStyle : { ...itemStyle, width: '100%' }}
          onClick={() => {
            const next = !isAktywnościExpanded;
            setIsAktywnościExpanded(next);
            if (next) {
              setIsSubscriptionsExpanded(false);
            }
          }}
        >
          SZJ
        </Label>
      </div>

      {isAktywnościExpanded && !isIconOnly && (
        <div
          style={{
            width: '100%',
            paddingLeft: '48px',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            gap: '2px',
            display: 'flex',
            position: 'relative',
          }}
        >
          <div
            className="border-l-2 border-[#DCDDE0] shrink-0"
            style={{
              position: 'absolute',
              left: '20px',
              top: 0,
              height: '56px',
            }}
          />
          {renderSzjSubItems(() => {})}
        </div>
      )}

      <SideNavFlyout
        open={isAktywnościExpanded && isIconOnly}
        triggerRef={szjTriggerRef}
        onClose={() => setIsAktywnościExpanded(false)}
      >
        {renderSzjSubItems(() => setIsAktywnościExpanded(false))}
      </SideNavFlyout>

      <Label
        size="Large"
        labelType="Square"
        leftIcon={
          isOnZgłoszeniaPage ? (
            <InboxSolid className="size-6" />
          ) : (
            <InboxOutline className="size-6" strokeWidth={1.5} />
          )
        }
        isIconOnly={isIconOnly}
        selected={isOnZgłoszeniaPage}
        style={itemStyle}
        onClick={() => navigate('/Zgłoszenia', { state: { freshStart: true } })}
      >
        Zgłoszenia
      </Label>

      {isAdminOrSuperAdmin && (
        <Label
          size="Large"
          labelType="Square"
          leftIcon={
            isOnFormularzeZadańPage ? (
              <DocumentDuplicateSolid className="size-6" />
            ) : (
              <DocumentDuplicateOutline className="size-6" strokeWidth={1.5} />
            )
          }
          isIconOnly={isIconOnly}
          selected={isOnFormularzeZadańPage}
          style={itemStyle}
          onClick={() =>
            navigate('/Formularze-zadań', { state: { freshStart: true } })
          }
        >
          Formularze zadań
        </Label>
      )}

      {isAdminOrSuperAdmin && (
        <Label
          size="Large"
          labelType="Square"
          leftIcon={
            <Award
              size={24}
              strokeWidth={isOnCertyfikatyPage ? 2 : 1.5}
              fill={isOnCertyfikatyPage ? 'currentColor' : 'none'}
            />
          }
          isIconOnly={isIconOnly}
          selected={isOnCertyfikatyPage}
          style={itemStyle}
          onClick={() =>
            navigate('/Certyfikaty', { state: { freshStart: true } })
          }
        >
          Certyfikaty
        </Label>
      )}
    </div>
  );
};

export default SideNavContent;
